//deps
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useState } from "react";

//defs
import { AddMoneyAndDelinkWalletAnalyticHandlers, AddMoneyAndDelinkWalletProps } from "@payment-ui/interfaces";

//internal-deps
import { GenericAlert, useMountedRef, GenericAlertProps } from "@swiggy-private/core-ui";
import { TextButton } from "@swiggy-private/dls-web-core/dist/components/TextButton";

//styles
import styles from "./styles.scss";

//constants
import { TEXTS } from "@payment-ui/constants/index";

//helpers
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";
import {
    getDelinkWalletFailureTitle,
    getDelinkWalletSuccessTitle,
    getDelinkWalletCTA,
    getDelinkWalletAlertTitle,
    getDelinkWalletAlertSubTitle,
    getDelinkWalletSuccessSubTitle,
} from "@payment-ui/helpers/Wallet";

//widgets
import { SuccessBsWithRedirect } from "@payment-ui/widgets/success-bs-redirect";

//data-test-id
import { ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface DelinkWalleyAnalyticsHandler {
    onClickTryAgainDelink: AddMoneyAndDelinkWalletAnalyticHandlers["onClickTryAgainDelink"];
    onClickCrossDelinkFailure: AddMoneyAndDelinkWalletAnalyticHandlers["onClickCrossDelinkFailure"];
    onClickCancelDelink: AddMoneyAndDelinkWalletAnalyticHandlers["onClickCancelDelink"];
    onClickConfirmDelink: AddMoneyAndDelinkWalletAnalyticHandlers["onClickConfirmDelink"];
    onImpressionConfirmDelink: AddMoneyAndDelinkWalletAnalyticHandlers["onImpressionConfirmDelink"];
    onClickDelinkCta: AddMoneyAndDelinkWalletAnalyticHandlers["onClickDelinkCta"];
    onImpressionFaliedDelink: AddMoneyAndDelinkWalletAnalyticHandlers["onImpressionFaliedDelink"];
    onImpressionSuccessDelink: AddMoneyAndDelinkWalletAnalyticHandlers["onImpressionSuccessDelink"];
}

interface DelinkWalletProps {
    onDelinkWallet: AddMoneyAndDelinkWalletProps["onDelinkWallet"];
    isDelinkSuccess: boolean;
    isDelinkFailure: boolean;
    onCloseSuccessBottomSheet: VoidFunction;
    onCloseFailureBottomSheet: VoidFunction;
    walletDetails: AddMoneyAndDelinkWalletProps["walletDetails"];
    analyticHandlers: DelinkWalleyAnalyticsHandler;
}

function DelinkWallet({
    onDelinkWallet,
    isDelinkSuccess,
    onCloseSuccessBottomSheet,
    onCloseFailureBottomSheet,
    isDelinkFailure,
    walletDetails,
    analyticHandlers,
}: DelinkWalletProps): ReactElement {
    const {
        onClickCancelDelink,
        onClickConfirmDelink,
        onClickCrossDelinkFailure,
        onClickTryAgainDelink,
        onImpressionConfirmDelink,
        onClickDelinkCta,
        onImpressionFaliedDelink,
        onImpressionSuccessDelink,
    } = analyticHandlers;

    const [showDelinkConfirmation, setShowDelinkConfirmation] = useState(false);
    const isMountedRef = useMountedRef();

    useEffect(() => {
        if (isDelinkSuccess) {
            onImpressionSuccessDelink();
        }
    }, [isDelinkSuccess, onImpressionSuccessDelink]);

    useEffect(() => {
        if (isDelinkFailure) {
            onImpressionFaliedDelink();
        }
    }, [isDelinkFailure, onImpressionFaliedDelink]);

    useEffect(() => {
        if (showDelinkConfirmation) {
            onImpressionConfirmDelink();
        }
    }, [onImpressionConfirmDelink, showDelinkConfirmation]);

    const onDelinkConfirmationClose = useCallback(() => {
        if (isMountedRef.current) {
            setShowDelinkConfirmation(false);
        }
    }, [isMountedRef]);

    const onDelinkCtaClick = useCallback(() => {
        onClickDelinkCta();
        if (isMountedRef.current) {
            setShowDelinkConfirmation(true);
        }
    }, [isMountedRef, onClickDelinkCta]);

    const onClickDelinkConfirmCta = useCallback(() => {
        onClickConfirmDelink();
        onDelinkWallet();
    }, [onClickConfirmDelink, onDelinkWallet]);

    const onClickDelinkCancelCta = useCallback(() => {
        onClickCancelDelink();
    }, [onClickCancelDelink]);

    const onClickDelinkTryAgainCta = useCallback(() => {
        onDelinkWallet();
        onClickTryAgainDelink();
    }, [onClickTryAgainDelink, onDelinkWallet]);

    const delinkFailureActions = useMemo(
        () => ({
            text: capitalizeFirstLetter(TEXTS.TRY_AGAIN),
            onClick: onClickDelinkTryAgainCta,
            className: styles.delinkWalletFailurePrimaryCta,
        }),
        [onClickDelinkTryAgainCta],
    );

    const delinkConfirmationActions = useMemo(
        () => ({
            primaryAction: { text: TEXTS.CONFIRM, onClick: onClickDelinkConfirmCta },
            secondaryAction: {
                text: TEXTS.CANCEL,
                className: styles.delinkWalletConfirmationSecondaryCta,
                onClick: onClickDelinkCancelCta,
            },
        }),
        [onClickDelinkCancelCta, onClickDelinkConfirmCta],
    );

    const delinkFailureTitle = useMemo(() => getDelinkWalletFailureTitle(walletDetails), [walletDetails]);

    const delinkSuccessTitle = useMemo(() => getDelinkWalletSuccessTitle(walletDetails), [walletDetails]);

    const delinkSuccessSubTitle = useMemo(() => getDelinkWalletSuccessSubTitle(walletDetails), [walletDetails]);

    const delinkCTA = useMemo(() => getDelinkWalletCTA(walletDetails), [walletDetails]);

    const genericAlertProps = useMemo(() => {
        if (showDelinkConfirmation) {
            return {
                onClose: onDelinkConfirmationClose,
                primaryAction: delinkConfirmationActions.primaryAction,
                secondaryAction: delinkConfirmationActions.secondaryAction,
                hideIcon: true,
                hideCloseIcon: true,
                title: getDelinkWalletAlertTitle(walletDetails),
                subTitle: getDelinkWalletAlertSubTitle(walletDetails),
                titleClassName: styles.delinkWalletConfirmationTitle,
                hideOnOverlayClick: true,
                primaryCtaDataTestId: ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_CONFIRMATION_PRIMARY_CTA,
                secondaryCtaDataTestId: ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_CONFIRMATION_SECONDARY_CTA,
                titleDataTestId: ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_CONFIRMATION_TITLE,
            };
        }
        if (isDelinkFailure) {
            return {
                onClose: onCloseFailureBottomSheet,
                onClickCloseButton: onClickCrossDelinkFailure,
                primaryAction: delinkFailureActions,
                title: delinkFailureTitle,
                titleClassName: styles.delinkWalletFailureTitle,
                containerClassName: styles.delinkWalletFailureContainer,
                closeButtonClassName: styles.delinkWalletFailureCloseButton,
                hideOnOverlayClick: true,
                primaryCtaDataTestId: ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_FAILURE_PRIMARY_CTA,
                titleDataTestId: ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_FAILURE_TITLE,
                closeButtonDataTestId: ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_FAILURE_CLOSE_CTA,
            };
        }
    }, [
        delinkConfirmationActions.primaryAction,
        delinkConfirmationActions.secondaryAction,
        delinkFailureActions,
        delinkFailureTitle,
        isDelinkFailure,
        onClickCrossDelinkFailure,
        onCloseFailureBottomSheet,
        onDelinkConfirmationClose,
        showDelinkConfirmation,
        walletDetails,
    ]);

    return (
        <div className={styles.delinkWallet}>
            <TextButton
                color="primary"
                buttonSize="medium"
                onClick={onDelinkCtaClick}
                data-testid={ADD_MONEY_DELINK_WALLET_DATA_TEST_IDS.DELINK_CTA}
            >
                {delinkCTA}
            </TextButton>
            {(showDelinkConfirmation || isDelinkFailure) && (
                <GenericAlert {...(genericAlertProps as GenericAlertProps)} />
            )}
            {isDelinkSuccess && (
                <SuccessBsWithRedirect
                    onClose={onCloseSuccessBottomSheet}
                    title={delinkSuccessTitle}
                    durationInSec={3}
                    subTitle={delinkSuccessSubTitle}
                    contentClassName={styles.successBottomSheetContent}
                    headerClassName={styles.successBottomSheetHeader}
                />
            )}
        </div>
    );
}

export default memo(DelinkWallet);
